import RoutePattern from 'route-pattern'
import { TidewaysContext } from './app/context'
import { User } from './domain/User'
import httpBuildQuery from './httpBuildQuery'

export interface BackendRoute {
    path: string
    skipHistoryApi: boolean
}

class Router {
    public history: any
    public routes: { [name: string]: BackendRoute }
    public tidewaysContext: TidewaysContext
    public user: User | null
    public parameters: object
    public parameterMatcher: RegExp
    public routeAliases: { [from: string]: string }

    constructor(history: any, routes: { [name: string]: BackendRoute }, tidewaysContext: TidewaysContext, user: User | null) {
        this.history = history
        this.routes = routes
        this.tidewaysContext = tidewaysContext
        this.user = user
        this.parameters = {
            application: tidewaysContext.application?.name,
            organization: tidewaysContext.organization?.name,
        }
        this.parameterMatcher = /\{([A-Za-z0-9]+)\}/g
        this.routeAliases = {}
    }

    public absoluteUrl = (route: string, parameters?: object): string => {
        const hostname = window.location.hostname
        const path = this.path(route, parameters)

        return `https://${hostname}${path}`
    }

    public path = (route: string, parameters?: object): string => {
        const allParameters = { ...this.parameters, ...(parameters || {}) }

        if (route in this.routeAliases) {
            route = this.routeAliases[route]
        }

        if (!(route in this.routes)) {
            // in jest tests, this might happen due to missing update of _fixtures/routes.ts
            throw new Error('Route ' + route + ' not defined.')
        }

        const keys: string[] = []
        let matches = this.parameterMatcher.exec(this.routes[route].path)
        while (matches) {
            keys.push(matches[1])
            matches = this.parameterMatcher.exec(this.routes[route].path)
        }

        const unknownKeys = [keys, Object.keys(allParameters)].reduce((firstArray, secondArray) =>
            firstArray.filter(element => !secondArray.includes(element)),
        )
        if (unknownKeys.length) {
            throw new Error('Missing values for ' + route + ': ' + unknownKeys.join(', '))
        }

        const queryKeys = parameters
            ? [Object.keys(parameters), keys].reduce((firstArray, secondArray) =>
                  firstArray.filter(element => !secondArray.includes(element)),
              )
            : []
        let query = ''
        if (queryKeys.length && parameters) {
            query = '?' + httpBuildQuery(this.pick(parameters, queryKeys))
        }

        return keys.reduce((link, key) => link.replace('{' + key + '}', allParameters[key]), this.routes[route].path) + query
    }

    public match = (path: string): object => {
        for (const route in this.routes) {
            if (Object.prototype.hasOwnProperty.call(this.routes, route)) {
                // @TODO: Cache route to pattern compilation?
                const pattern = RoutePattern.fromString(this.reactRoute(route))

                if (pattern.matches(path)) {
                    return {
                        route,
                        parameters: pattern.match(path).namedParams || {},
                    }
                }
            }
        }

        throw new Error('No defined route match path: ' + path)
    }

    public shouldSkipHistoryApi = (route: string): boolean => this.routes[route]?.skipHistoryApi ?? false

    /**
     * @deprecated Use push() and replace() directly
     */
    public goTo = (route: string, parameters: object, replaceState?: string): void => {
        if (replaceState) {
            this.push(route, parameters)
        } else {
            this.replace(route, parameters)
        }
    }

    public push = (route: string, parameters: object): void => {
        this.history.push(this.path(route, parameters))
    }

    public replace = (route: string, parameters: object): void => {
        this.history.replace(this.path(route, parameters))
    }

    public reactRoute = (route: string): string => {
        if (!(route in this.routes)) {
            throw new Error('Route ' + route + ' not defined.')
        }

        const keys: string[] = []
        let matches = this.parameterMatcher.exec(this.routes[route].path)
        while (matches) {
            keys.push(matches[1])
            matches = this.parameterMatcher.exec(this.routes[route].path)
        }

        return keys.reduce((link, key) => {
            return link.replace('{' + key + '}', ':' + key)
        }, this.routes[route].path)
    }

    public setDefaultParameter = (name: string, value: string): void => {
        this.parameters[name] = value
    }

    public getContext = (): TidewaysContext => {
        return this.tidewaysContext
    }

    public getCurrentUser = (): User | null => this.user

    private pick = (object: object, keys: string[]): object => {
        return keys.reduce((obj, key) => {
            if (object && Object.prototype.hasOwnProperty.call(object, key)) {
                obj[key] = object[key]
            }
            return obj
        }, {})
    }
}

export default Router
